import { chain } from '@/config/chain'
import { getLockupStats, getPriceSeries, getUserHistories } from '@/services/api'
import type { LockupStats, WalletHistory } from '@/services/api'
import { formatEthDisplay } from '@/utils/format'
import { MainLayout } from '@components/layouts/MainLayout'
import { useWallet } from '@components/providers/WalletProvider'
import { DataStatistic } from '@components/ui/DataStatistic'
import { IconButton } from '@components/ui/IconButton'
import type { MarketRange, MarketSeriesMap } from '@components/ui/MarketCard'
import { MarketCard } from '@components/ui/MarketCard'
import { SettingsSheet } from '@components/ui/SettingsSheet'
import { useWallets } from '@privy-io/react-auth'
import type { UTCTimestamp } from 'lightweight-charts'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Address, createPublicClient, custom, erc20Abi, formatEther, formatUnits } from 'viem'

import ArcsTokenIcon from '@/components/ui/ArcsTokenIcon'
import ethereumTokenIcon from '../../public/icons/EthereumTokenIcon.svg'

const currencyFormatter = (value: number) => {
  const abs = Math.abs(value)
  const fractionDigits = abs < 1 ? 4 : 2
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(abs)
}

const formatChangeLabel = (absolute: number, percent: number | null) => {
  const absoluteText = `${absolute >= 0 ? '+' : '-'}${currencyFormatter(absolute)}`
  if (percent == null) return absoluteText
  const percentText = `${percent >= 0 ? '+' : '-'}${Math.abs(percent).toFixed(2)}%`
  return `${absoluteText} (${percentText})`
}

const tokenFormatter = (value: number) => {
  const abs = Math.abs(value)
  const fractionDigits = abs < 1 ? 4 : 2
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

/** ETH残高フォーマット（18桁精度、末尾の0は削除） */
const formatEthTokenAmount = (value: number) => formatEthDisplay(value)

const createYenFormatter = () =>
  new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const formatFiat = (value: number) => createYenFormatter().format(value)

const formatSignedFiat = (value: number) => {
  const unsigned = createYenFormatter().format(Math.abs(value))
  return `${value >= 0 ? '+' : '-'}${unsigned}`
}

const formatSignedPercent = (value: number) => {
  if (!Number.isFinite(value)) return '+0.00%'
  return `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(2)}%`
}

const formatPercent = (value: number) => {
  if (!Number.isFinite(value)) return '0.00%'
  return `${value.toFixed(2)}%`
}

type TokenSummary = {
  key: 'arcs' | 'ethereum'
  name: string
  amount: string
  fiatValue: string
  rate: string
  changeValue: string
  changePercent: string | null
  isPositive: boolean
}

const parseNumeric = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export const Home: React.FC = () => {
  const { smartWalletAddress, isSmartWalletReady } = useWallet()
  const { wallets, ready: areWalletsReady } = useWallets()
  const navigate = useNavigate()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [currentPriceLabel, setCurrentPriceLabel] = useState('0.00')
  const [priceChangeLabel, setPriceChangeLabel] = useState('0.00')
  const [isPriceChangePositive, setIsPriceChangePositive] = useState(true)
  const [chartData, setChartData] = useState<Partial<MarketSeriesMap>>({})
  const [arcsSpotPrice, setArcsSpotPrice] = useState<number | null>(null)
  const [arcsPriceChange, setArcsPriceChange] = useState<{
    absolute: number | null
    percent: number | null
  } | null>(null)
  const [ethSpotPrice, setEthSpotPrice] = useState<number | null>(null)
  const [ethPriceChange, setEthPriceChange] = useState<{
    absolute: number | null
    percent: number | null
  } | null>(null)
  const [lockupStats, setLockupStats] = useState<LockupStats | null>(null)
  const [ethBalance, setEthBalance] = useState<bigint | null>(null)
  const [arcsOnchainBalance, setArcsOnchainBalance] = useState<bigint | null>(null)
  const [histories, setHistories] = useState<WalletHistory[]>([])

  const arcsTokenAddress = import.meta.env.VITE_ARCS_TOKEN_ADDRESS

  useEffect(() => {
    const controller = new AbortController()

    const fetchMarketData = async () => {
      try {
        const payload = await getPriceSeries('ARCS/JPY', controller.signal)

        if (payload.latestPrice?.value != null) {
          setCurrentPriceLabel(currencyFormatter(payload.latestPrice.value))
          setArcsSpotPrice(payload.latestPrice.value)
        } else {
          setCurrentPriceLabel('0.00')
          setArcsSpotPrice(null)
        }

        if (payload.change24h?.absolute != null) {
          setIsPriceChangePositive(payload.change24h.absolute >= 0)
          setPriceChangeLabel(
            formatChangeLabel(payload.change24h.absolute, payload.change24h.percent)
          )
        } else {
          setIsPriceChangePositive(true)
          setPriceChangeLabel('0.00')
        }
        setArcsPriceChange(payload.change24h ?? { absolute: null, percent: null })

        if (payload.series) {
          setChartData(
            Object.entries(payload.series).reduce<Partial<MarketSeriesMap>>(
              (acc, [range, points]) => {
                if (!points || points.length === 0) return acc
                const sanitized = points
                  .map((point) => ({
                    time: point.time as UTCTimestamp,
                    value: point.value,
                  }))
                  .filter((point) => Number.isFinite(point.value) && Number.isFinite(point.time))
                if (sanitized.length === 0) return acc
                acc[range as MarketRange] = sanitized
                return acc
              },
              {}
            )
          )
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        console.error('[wallet-web] Unexpected error while loading price series', error)
        setIsPriceChangePositive(true)
        setPriceChangeLabel('0.00')
        setArcsSpotPrice(null)
        setArcsPriceChange({ absolute: null, percent: null })
      }
    }

    fetchMarketData()

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const fetchEthMarketData = async () => {
      try {
        const payload = await getPriceSeries('ETH/JPY', controller.signal)
        if (payload.latestPrice?.value != null) {
          setEthSpotPrice(payload.latestPrice.value)
        } else {
          setEthSpotPrice(null)
        }
        setEthPriceChange(payload.change24h ?? { absolute: null, percent: null })
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        console.error('[wallet-web] Unexpected error while loading ETH price series', error)
        setEthSpotPrice(null)
        setEthPriceChange({ absolute: null, percent: null })
      }
    }

    fetchEthMarketData()

    return () => {
      controller.abort()
    }
  }, [])

  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true)
  }, [])

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false)
  }, [])

  const handleOpenNotifications = useCallback(() => {
    navigate('/notifications')
  }, [navigate])

  useEffect(() => {
    const controller = new AbortController()

    const fetchLockupStats = async () => {
      try {
        const payload = await getLockupStats(controller.signal)
        setLockupStats(payload.stats)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        console.error('[wallet-web] Unexpected error while loading lockup stats', error)
        setLockupStats(null)
      }
    }

    fetchLockupStats()

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const fetchHistories = async () => {
      try {
        const payload = await getUserHistories({ limit: 200, signal: controller.signal })
        setHistories(payload.histories)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        console.error('[wallet-web] Unexpected error while loading histories', error)
        setHistories([])
      }
    }

    fetchHistories()

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadEthBalance = async () => {
      if (!areWalletsReady || !smartWalletAddress || !isSmartWalletReady) {
        setEthBalance(null)
        return
      }

      const normalizedAddress = smartWalletAddress.toLowerCase()
      const targetWallet =
        wallets.find((wallet) => wallet.address.toLowerCase() === normalizedAddress) ?? wallets[0]

      if (!targetWallet) {
        setEthBalance(null)
        return
      }

      try {
        const provider = await targetWallet.getEthereumProvider()

        const publicClient = createPublicClient({
          chain,
          transport: custom(provider),
        })

        const [nativeBalance, arcsBalance] = await Promise.all([
          publicClient.getBalance({
            address: smartWalletAddress as Address,
          }),
          arcsTokenAddress
            ? publicClient
                .readContract({
                  address: arcsTokenAddress as Address,
                  abi: erc20Abi,
                  functionName: 'balanceOf',
                  args: [smartWalletAddress as Address],
                })
                .catch((error) => {
                  console.error('[wallet-web] Failed to load ARCS on-chain balance', error)
                  return null
                })
            : Promise.resolve(null),
        ])

        if (cancelled) return

        console.log('[wallet-web] ETH balance loaded via viem', {
          queriedAddress: smartWalletAddress,
          providerAddress: targetWallet.address,
          chainId: chain.id.toString(),
          balance: nativeBalance.toString(),
        })

        if (!arcsTokenAddress) {
          console.warn(
            '[wallet-web] VITE_ARCS_TOKEN_ADDRESS is not configured; ARCS on-chain balance skipped'
          )
        } else if (arcsBalance != null) {
          console.log('[wallet-web] ARCS on-chain balance loaded via viem', {
            tokenAddress: arcsTokenAddress,
            queriedAddress: smartWalletAddress,
            balance: arcsBalance.toString(),
          })
        }

        setEthBalance(nativeBalance)
        setArcsOnchainBalance(arcsBalance)
      } catch (error) {
        if (!cancelled) {
          console.error('[wallet-web] Unexpected error while loading smart wallet balance', error)
          setEthBalance(null)
          setArcsOnchainBalance(null)
        }
      }
    }

    void loadEthBalance()

    return () => {
      cancelled = true
    }
  }, [areWalletsReady, isSmartWalletReady, smartWalletAddress, wallets])

  const currentlyLockedAmount = parseNumeric(lockupStats?.currentlyLocked)
  const arcsOnchainAmount =
    arcsOnchainBalance != null ? Number(formatUnits(arcsOnchainBalance, 18)) : 0
  const arcsTokenAmount = currentlyLockedAmount + arcsOnchainAmount
  const ethTokenAmount = ethBalance != null ? Number(formatEther(ethBalance)) : 0

  const arcsFiatValue = arcsSpotPrice != null ? arcsTokenAmount * arcsSpotPrice : 0
  const ethFiatValue = ethSpotPrice != null ? ethTokenAmount * ethSpotPrice : 0

  const arcsHoldingsChange =
    arcsPriceChange?.absolute != null ? arcsTokenAmount * arcsPriceChange.absolute : null
  const ethHoldingsChange =
    ethPriceChange?.absolute != null ? ethTokenAmount * ethPriceChange.absolute : null

  const arcsTokenAmountLabel = `${tokenFormatter(arcsTokenAmount)} ARX`
  const arcsLockedLabel = `${tokenFormatter(currentlyLockedAmount)} ARX`

  const arcsHoldingsChangeValueLabel =
    arcsHoldingsChange != null ? formatSignedFiat(arcsHoldingsChange) : '--'
  const arcsHoldingsChangePercentLabel =
    arcsPriceChange?.percent != null ? formatSignedPercent(arcsPriceChange.percent) : null

  const ethTokenAmountLabel = `${formatEthTokenAmount(ethTokenAmount)} ETH`
  const ethHoldingsChangeValueLabel =
    ethHoldingsChange != null ? formatSignedFiat(ethHoldingsChange) : '--'
  const ethHoldingsChangePercentLabel =
    ethPriceChange?.percent != null ? formatSignedPercent(ethPriceChange.percent) : null

  const {
    averageAcquisitionPriceValue,
    averageAcquisitionPriceLabel,
    averageAcquisitionChangePercent,
    averageAcquisitionChangePercentValue,
  } = useMemo(() => {
    const arcsHistories = histories.filter((history) => history.asset.symbol === 'ARX')

    let totalAmount = 0
    let totalCost = 0

    for (const history of arcsHistories) {
      const parsedAmount = parseNumeric(history.amount)
      if (parsedAmount <= 0) continue

      const description = history.description ?? ''

      const unitPriceMatch = description.match(/取得単価[:：]\s*([0-9]+(?:\.[0-9]+)?)/i)
      const unitPrice = unitPriceMatch ? Number(unitPriceMatch[1]) : null

      if (unitPrice != null) {
        totalAmount += parsedAmount
        totalCost += parsedAmount * unitPrice
      }
    }

    if (totalAmount === 0) {
      return {
        averageAcquisitionPriceValue: null,
        averageAcquisitionPriceLabel: '--',
        averageAcquisitionChangePercent: null,
        averageAcquisitionChangePercentValue: null,
      }
    }

    const averagePrice = totalCost / totalAmount
    const label = formatFiat(averagePrice)
    let changePercent: string | null = null
    let changePercentValue: number | null = null
    if (averagePrice === 0) {
      changePercent = arcsSpotPrice != null && arcsSpotPrice > 0 ? '∞' : '--'
    } else if (arcsSpotPrice != null) {
      changePercentValue = (arcsSpotPrice / averagePrice) * 100
      changePercent = formatPercent(changePercentValue)
    }

    return {
      averageAcquisitionPriceValue: averagePrice,
      averageAcquisitionPriceLabel: label,
      averageAcquisitionChangePercent: changePercent,
      averageAcquisitionChangePercentValue: changePercentValue,
    }
  }, [histories, arcsSpotPrice])

  const arcsRateLabel = arcsSpotPrice != null ? formatFiat(arcsSpotPrice) : '--'
  const ethRateLabel = ethSpotPrice != null ? formatFiat(ethSpotPrice) : '--'

  const arcsChangeColor =
    arcsHoldingsChange == null
      ? 'text-[#9f9f9f]'
      : arcsHoldingsChange >= 0
        ? 'text-[#0bad7a]'
        : 'text-[#f04438]'

  const tokenSummaries: TokenSummary[] = [
    {
      key: 'arcs',
      name: 'ARCS',
      amount: arcsTokenAmountLabel,
      fiatValue: formatFiat(arcsFiatValue),
      rate: arcsRateLabel,
      changeValue: arcsHoldingsChangeValueLabel,
      changePercent: arcsHoldingsChangePercentLabel,
      isPositive: (arcsHoldingsChange ?? 0) >= 0,
    },
    {
      key: 'ethereum',
      name: 'Ethereum',
      amount: ethTokenAmountLabel,
      fiatValue: formatFiat(ethFiatValue),
      rate: ethRateLabel,
      changeValue: ethHoldingsChangeValueLabel,
      changePercent: ethHoldingsChangePercentLabel,
      isPositive: (ethHoldingsChange ?? 0) >= 0,
    },
  ]

  return (
    <>
      <MainLayout
        activeMain="home"
        header={{
          enabled: true,
          title: '',
          leftIcon: null,
          rightIcon: null,
          leftSlot: (
            <IconButton aria-label="通知" icon="Notification" onClick={handleOpenNotifications} />
          ),
          rightSlot: <IconButton aria-label="メニュー" icon="Dots" onClick={handleOpenSettings} />,
          className: 'flex h-16 w-full items-center justify-between px-4',
          containerClassName: 'bg-white pt-4',
          offset: 64,
        }}
        contentClassName="pb-10"
      >
        <section className="">
          <div className="flex flex-col gap-4 rounded-b-[24px] bg-white py-6">
            <div className="flex items-center gap-2 px-4">
              <ArcsTokenIcon className="size-[20px]" />
              <span className="text-[16px] font-medium tracking-[0.32px] text-[#71717a]">
                ARCS残高
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4">
              <p className="text-[48px] font-medium leading-none tracking-[-0.05em] text-[#1f1f1f]">
                {formatFiat(arcsFiatValue)}
              </p>
              <p className="text-[44px] font-medium leading-none tracking-[-0.05em] text-[#9f9f9f]">
                {arcsTokenAmountLabel}
              </p>
            </div>
            <div className="flex items-center gap-1 px-4">
              <span className={`text-[18px] font-medium tracking-[-0.36px] ${arcsChangeColor}`}>
                {arcsHoldingsChangeValueLabel}
              </span>
              {arcsHoldingsChangePercentLabel ? (
                <span className="text-[18px] font-medium tracking-[-0.36px] text-[#9f9f9f]">
                  ({arcsHoldingsChangePercentLabel})
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 px-4">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-medium tracking-[-0.28px] text-[#9f9f9f]">
                  ロックアップ残高
                </span>
                <span className="text-[18px] font-medium tracking-[-0.36px] text-[#71717a]">
                  {arcsLockedLabel}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 border-t border-[#e7ecfb] px-4 pt-4">
              <DataStatistic
                className="flex grow"
                label="平均取得単価"
                value={averageAcquisitionPriceLabel}
                tone={
                  averageAcquisitionPriceValue == null ||
                  !Number.isFinite(averageAcquisitionPriceValue)
                    ? 'muted'
                    : 'default'
                }
              />
              <DataStatistic
                className="flex grow border-l border-[#e7ecfb] pl-4"
                label="平均取得単価騰落率"
                value={averageAcquisitionChangePercent ?? '--'}
                tone={
                  averageAcquisitionChangePercent == null
                    ? 'muted'
                    : averageAcquisitionChangePercent === '∞'
                      ? 'positive'
                      : (averageAcquisitionChangePercentValue ?? 0) < 100
                        ? 'negative'
                        : 'positive'
                }
              />
            </div>
          </div>
        </section>

        <section className="pt-8 px-4">
          <p className="text-[16px] font-medium text-[#7b849b]">Market</p>
          <MarketCard
            className="mt-4 shadow-[0_4px_8px_rgba(0,0,0,0.02)]"
            tokenLabel="ARCS"
            currentPriceLabel={currentPriceLabel}
            priceChangeLabel={priceChangeLabel}
            isPriceChangePositive={isPriceChangePositive}
            data={chartData}
          />
        </section>

        <section className="pt-8 px-4 pb-6">
          <p className="text-[16px] font-medium text-[#7b849b]">Tokens</p>
          <div className="flex flex-col gap-2 mt-4">
            {tokenSummaries.map((summary) => {
              return (
                <div
                  key={summary.key}
                  className="flex items-center justify-between gap-4 rounded-[16px] bg-white p-4 shadow-[0_4px_8px_rgba(0,0,0,0.02)]"
                >
                  <div className="flex items-center gap-4">
                    {summary.key === 'arcs' ? (
                      <ArcsTokenIcon className="size-[48px]" />
                    ) : (
                      <img
                        src={ethereumTokenIcon}
                        alt=""
                        aria-hidden="true"
                        className="size-[48px]"
                      />
                    )}
                    <div className="flex flex-col">
                      <p className="text-base font-semibold text-[#1f1f1f]">{summary.name}</p>
                      <p className="text-sm font-medium text-[#7b849b]">{summary.rate}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <p className="text-base font-semibold text-[#1f1f1f]">{summary.fiatValue}</p>
                    <p className="text-sm font-medium text-[#7b849b]">{summary.amount}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      </MainLayout>
      <SettingsSheet open={isSettingsOpen} onClose={handleCloseSettings} />
    </>
  )
}

export default Home
